import { REPLAY_VERSION, SIM_TICK } from "./constants";
import {
  buildProducer,
  buildRoute,
  buildRouteVia,
  buyLane,
  buyTruck,
  expandRegion,
  sellRoute,
  sellTruck,
  step,
  upgradeProducer,
} from "./sim";
import type { GameState, GoodId, ProducedGoodId } from "./types";
import { createGame } from "./world";

export type ReplayAction =
  | { readonly type: "buildRoute"; readonly from: number; readonly to: number; readonly good?: GoodId | undefined }
  | { readonly type: "buildRouteVia"; readonly from: number; readonly to: number; readonly via: number; readonly good?: GoodId | undefined }
  | { readonly type: "buyTruck"; readonly routeId: number }
  | { readonly type: "sellTruck"; readonly routeId: number }
  | { readonly type: "sellRoute"; readonly routeId: number }
  | { readonly type: "buyLane"; readonly corridorId: number }
  | { readonly type: "upgradeProducer"; readonly nodeId: number }
  | { readonly type: "buildProducer"; readonly good: ProducedGoodId }
  | { readonly type: "expandRegion" };

export type ReplayCommand = ReplayAction & { readonly tick: number };

/** Applies one player action. Callers record only a successful command. */
export function applyReplayCommand(state: GameState, command: ReplayCommand): boolean {
  switch (command.type) {
    case "buildRoute":
      return buildRoute(state, command.from, command.to, command.good).ok;
    case "buildRouteVia":
      return buildRouteVia(state, command.from, command.to, command.via, command.good).ok;
    case "buyTruck":
      return buyTruck(state, command.routeId);
    case "sellTruck":
      return sellTruck(state, command.routeId);
    case "sellRoute": {
      const exists = state.routes.some((route) => route.id === command.routeId);
      if (exists) sellRoute(state, command.routeId);
      return exists;
    }
    case "buyLane":
      return buyLane(state, command.corridorId);
    case "upgradeProducer":
      return upgradeProducer(state, command.nodeId);
    case "buildProducer":
      return buildProducer(state, command.good);
    case "expandRegion":
      return expandRegion(state).ok;
  }
}

/** Reconstructs a world from its seed and commands through their final tick. */
export function replayWorld(seed: number, commands: readonly ReplayCommand[], endTick = commands.at(-1)?.tick ?? -1): GameState {
  const state = createGame(seed);
  let index = 0;
  for (let tick = 0; tick <= endTick; tick++) {
    while (commands[index]?.tick === tick) {
      applyReplayCommand(state, commands[index]!);
      index++;
    }
    step(state, SIM_TICK);
  }
  // Commands the player issued after the last tick that ran. The recorder stamps
  // an action with the tick it will precede, and a paused game advances no ticks
  // at all — so every road built while paused sits one past `endTick`. Dropping
  // them here would hand the player back a world without the buildings they had
  // just paid for.
  while (index < commands.length) {
    applyReplayCommand(state, commands[index]!);
    index++;
  }
  return state;
}

export { REPLAY_VERSION };
